"use client";

import { useState } from "react";
import { ofetch } from "ofetch";
import useSWR from "swr";
import * as z from "zod";

import type { GetOfficesApiSuccessResponse } from "@/api/offices/route";
import { Address } from "@/components/atoms/Address";
import Button from "@/components/atoms/Button";
import InputContainer from "@/components/forms/controls/InputContainer";
import {
  LocationValueSelectProps,
  NewLocationInput,
} from "@/components/forms/controls/NewLocationInput";
import Form from "@/components/forms/Form";
import { env } from "@/env";
import { GetOfficesQuery, Office } from "@/graphql/sdk/__generated__";
import { getDistanceInKm } from "@/helpers/distance";
import { useZodForm } from "@/hooks/useZodForm";
import { AllowedLanguages } from "@/types/generic";

const fetcher = async (url: string) => {
  return await ofetch<GetOfficesApiSuccessResponse>(url);
};

interface OfficeSelectorFormProps {
  i18n: {
    error_required: string;
    search_location_label: string;
    search_location_placeholder: string;
    select_office: string;
    use_current_location: string;
  };
  locale: AllowedLanguages;
  onSelectOffice: (office: Office) => void;
}

const OFFICES_LIMIT = 10;
const MAX_DISTANCE = 20;

export const OfficeSelectorForm = ({
  locale,
  i18n,
  onSelectOffice,
}: OfficeSelectorFormProps) => {
  const schema = z.object({
    city: z.string().length(4, i18n.error_required),
  });

  const { data, error, isLoading } = useSWR(
    `${env.NEXT_PUBLIC_BASE_URL}/api/offices?langcode=${locale}`,
    fetcher,
  );

  const allOffices = data;
  const [closestOffices, setClosestOffices] =
    useState<GetOfficesQuery["offices"]>();

  const form = useZodForm({
    schema,
    defaultValues: {
      city: "",
    },
  });

  const updateLocation = (location: LocationValueSelectProps) => {
    findClosestOffices(location);
  };

  const findClosestOffices = (location: LocationValueSelectProps) => {
    const offices = allOffices?.filter((office) => {
      if (!office) {
        return false;
      }

      // Store distance in km in the office object
      if (
        location.latitude &&
        location.longitude &&
        office.latitude &&
        office.longitude
      ) {
        const distance = getDistanceInKm(
          location.latitude,
          location.longitude,
          office.latitude,
          office.longitude,
        );

        if (distance > Number(MAX_DISTANCE)) {
          return false;
        }

        // @ts-expect-error distance is not defined on Office, but we add it here
        office.distance = distance;
      }

      return true;
    });

    // Sort the offices array by distance (closest first)
    // @ts-expect-error we added distance to the office object
    offices?.sort((a, b) => a.distance - b.distance);

    setClosestOffices(offices?.slice(0, OFFICES_LIMIT));
  };

  return (
    <Form form={form} className="" noValidate={true}>
      <InputContainer isError={Boolean(error)}>
        <label htmlFor="office_selector_city" className="label-required">
          {i18n.search_location_label}
        </label>
        <NewLocationInput
          onSelectValue={updateLocation}
          className="w-full"
          inputClassName="form-input-lg"
          inputName="city"
          i18n={{
            placeholder: i18n.search_location_placeholder,
            use_current_location: i18n.use_current_location,
          }}
          id="office_selector_city"
          locale={locale}
        />
      </InputContainer>

      {closestOffices && (
        <div className="mt-4 max-h-96 divide-y overflow-y-auto border">
          {closestOffices.map((office: Office) => (
            <div
              key={office.id}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <div>
                <strong>{office.title}</strong>
                {office.address && (
                  <Address
                    address={office.address}
                    className="mb-0 grid-cols-[16px_1fr] gap-x-2 gap-y-1 text-sm"
                    locale={locale}
                  />
                )}
              </div>
              <Button
                type="button"
                className="btn-sm"
                arrow={false}
                onClick={() => onSelectOffice(office)}
              >
                {i18n.select_office}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Form>
  );
};
